import type { ReactNode, WheelEvent } from "react";
import { cn } from "@/lib/utils";

/** Spreadsheet-style chrome for document line items. */
export function LineGrid({
  children,
  className,
  minWidth = "56rem",
}: {
  children: ReactNode;
  className?: string;
  minWidth?: string;
}) {
  return (
    <div className={cn("rounded-md border border-border", className)}>
      {/* Horizontal scroll only — keep height open so the row chrome stays full-size.
          Entity pickers portal out of this container to avoid clipping. */}
      <div className="overflow-x-auto">
        <table
          className="w-full border-separate border-spacing-0 text-xs"
          style={{ minWidth, tableLayout: "fixed" }}
        >
          {children}
        </table>
      </div>
    </div>
  );
}

export function LineGridHead({ children }: { children: ReactNode }) {
  return (
    <thead className="sticky top-0 z-[1] bg-surface/95 backdrop-blur-sm">
      <tr>{children}</tr>
    </thead>
  );
}

export function LineGridTh({
  children,
  className,
  align = "left",
}: {
  children?: ReactNode;
  className?: string;
  align?: "left" | "right" | "center";
}) {
  return (
    <th
      className={cn(
        "h-[var(--fs-table-head-h)] border-b border-border px-1 text-2xs font-semibold uppercase tracking-[0.04em] text-muted-foreground",
        align === "right" && "text-right",
        align === "center" && "text-center",
        className,
      )}
    >
      {children}
    </th>
  );
}

export function LineGridBody({ children }: { children: ReactNode }) {
  return <tbody>{children}</tbody>;
}

export function LineGridRow({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <tr className={cn("hover:bg-muted/40 [&_td]:border-b [&_td]:border-border/60", className)}>
      {children}
    </tr>
  );
}

export function LineGridTd({
  children,
  className,
  align = "left",
}: {
  children?: ReactNode;
  className?: string;
  align?: "left" | "right" | "center";
}) {
  return (
    <td
      className={cn(
        "overflow-visible px-0.5 py-[var(--fs-row-py)] align-middle",
        "min-h-[calc(var(--fs-input-h)+2*var(--fs-row-py))]",
        align === "right" && "text-right",
        align === "center" && "text-center",
        className,
      )}
    >
      {children}
    </td>
  );
}

/** Visible dense control for cells inside LineGrid. */
export function cellControlClass(extra?: string) {
  return cn(
    "fs-control-cell w-full border border-border bg-card text-foreground",
    "hover:border-border-strong focus:border-ring focus:outline-none focus:ring-1 focus:ring-ring/30",
    "disabled:cursor-not-allowed disabled:opacity-60",
    extra,
  );
}

/** Block mouse-wheel from changing focused number inputs while scrolling the page. */
export function blockNumberWheel(e: WheelEvent<HTMLInputElement>) {
  e.currentTarget.blur();
}
